import axios, { isAxiosError, isCancel } from 'axios';

import { DEFAULT_PERSONA } from './neighbourhoodConstants';
import type {
  NeighbourhoodApiRequest,
  NeighbourhoodApiResponse,
  NeighbourhoodInsights,
  POIContext,
} from './neighbourhoodDescriptionTypes';

// Neighbourhood AI API configuration (nAIghborhood service)
const getLifestyleAIGuideEndpoint = (): string => {
  const endpoint = import.meta.env.VUE_LIFESTYLE_AI_GUIDE_API_ENDPOINT;
  if (!endpoint) {
    throw new Error('VUE_LIFESTYLE_AI_GUIDE_API_ENDPOINT environment variable is not configured');
  }
  return endpoint;
};

const getNeighbourhoodApiUrl = (): string => {
  return `${getLifestyleAIGuideEndpoint()}/naighbourhood-description`;
};

const getNeighbourhoodPOIApiUrl = (): string => {
  return `${getLifestyleAIGuideEndpoint()}/naighbourhood-poi-description`;
};

const API_TIMEOUT = 30000;

function mapPersonaToApi(persona?: string): NeighbourhoodApiRequest['persona'] {
  if (!persona) return DEFAULT_PERSONA;

  const lowerPersona = persona.toLowerCase();

  if (lowerPersona.includes('family') || lowerPersona.includes('children')) {
    return 'Family';
  }

  if (lowerPersona.includes('professional') || lowerPersona.includes('single')) {
    return 'Professional';
  }

  if (lowerPersona.includes('student') || lowerPersona.includes('wg')) {
    return 'Student';
  }

  return DEFAULT_PERSONA;
}

interface BuildInsightsRequestParams {
  countryCode: string;
  zipCode?: string;
  municipalityName: string;
  latitude: number;
  longitude: number;
  region?: string;
  persona?: string;
  locale?: string;
  propertyType?: string;
  listingId: string;
  googlePlacesContext?: {
    nearbyRestaurants?: string[];
    nearbySchools?: string[];
    nearbyParks?: string[];
    nearbyTransit?: string[];
    nearbyShopping?: string[];
  };
}

/**
 * Build request payload for neighbourhood insights API (Google Places version)
 */
function buildNeighbourhoodInsightsRequest(
  params: BuildInsightsRequestParams,
): NeighbourhoodApiRequest {
  const {
    countryCode,
    zipCode,
    municipalityName,
    latitude,
    longitude,
    region,
    persona,
    locale,
    propertyType,
    listingId,
    googlePlacesContext,
  } = params;

  return {
    listingId,
    location: {
      country: countryCode,
      postalCode: zipCode || '',
      locality: municipalityName,
      latitude,
      longitude,
      region,
    },
    persona: mapPersonaToApi(persona),
    ...(locale && { locale }),
    objectDescription: propertyType || 'Residential property',
    ...(googlePlacesContext && { googlePlacesContext }),
  };
}

/**
 * Track non-200 response error in Datadog (Google Places version)
 * Tracking temporarily disabled
 */
function trackNon200GooglePlacesError(
  _status: number,
  _municipalityName: string,
  _latitude: number,
  _longitude: number,
): void {
  // Tracking disabled
}

/**
 * Track invalid response structure error (Google Places version)
 * Tracking temporarily disabled
 */
function trackInvalidGooglePlacesResponseError(_municipalityName: string): void {
  // Tracking disabled
}

/**
 * Track Axios error for Google Places API
 * Tracking temporarily disabled
 */
function trackGooglePlacesAxiosError(
  _error: any,
  _municipalityName: string,
  _latitude: number,
  _longitude: number,
): void {
  // Tracking disabled
}

/**
 * Handle errors from Google Places neighbourhood API calls
 */
function handleGooglePlacesApiError(
  error: unknown,
  municipalityName: string,
  latitude: number,
  longitude: number,
): null {
  if (error instanceof Error && error.name === 'AbortError') {
    throw error;
  }

  if (isAxiosError(error)) {
    if (isCancel(error)) {
      throw new Error('AbortError');
    }
    trackGooglePlacesAxiosError(error, municipalityName, latitude, longitude);
  }

  return null;
}

/**
 * Generate neighbourhood insights by calling AWS Lambda endpoint
 * The Lambda function handles OpenAI integration server-side
 */
export async function generateNeighbourhoodInsights(
  params: {
    latitude: number;
    longitude: number;
    municipalityName: string;
    countryCode?: string;
    propertyType?: string;
    zipCode?: string;
    region?: string;
    persona?: string;
    locale?: string;
    listingId: string;
    googlePlacesContext?: {
      nearbyRestaurants?: string[];
      nearbySchools?: string[];
      nearbyParks?: string[];
      nearbyTransit?: string[];
      nearbyShopping?: string[];
    };
  },
  signal?: AbortSignal,
): Promise<NeighbourhoodInsights | null> {
  const {
    latitude,
    longitude,
    municipalityName,
    countryCode = 'CH',
    propertyType,
    zipCode,
    region,
    persona,
    locale,
    googlePlacesContext,
    listingId,
  } = params;

  try {
    const requestPayload = buildNeighbourhoodInsightsRequest({
      countryCode,
      zipCode,
      municipalityName,
      latitude,
      longitude,
      region,
      persona,
      locale,
      propertyType,
      listingId,
      googlePlacesContext,
    });

    const response = await axios.post<NeighbourhoodApiResponse>(
      getNeighbourhoodApiUrl(),
      requestPayload,
      {
        headers: { 'Content-Type': 'application/json' },
        timeout: API_TIMEOUT,
        validateStatus: (status) => status < 500,
        signal,
      },
    );

    if (response.status !== 200) {
      trackNon200GooglePlacesError(response.status, municipalityName, latitude, longitude);
      return null;
    }

    const apiData = (response.data as any).data || response.data;
    const insights = mapApiResponseToInsights(apiData);

    if (!insights.description && !(response.data as any).neighbourhoodDescription) {
      trackInvalidGooglePlacesResponseError(municipalityName);
      return null;
    }

    return insights;
  } catch (error) {
    return handleGooglePlacesApiError(error, municipalityName, latitude, longitude);
  }
}

interface BuildApiRequestParams {
  countryCode: string;
  zipCode?: string;
  municipalityName: string;
  latitude: number;
  longitude: number;
  region?: string;
  persona?: string;
  locale?: string;
  propertyType?: string;
  listingId: string;
  poiContext?: POIContext;
  enableWebSearch?: boolean;
}

/**
 * Build request payload for neighbourhood POI API
 */
function buildNeighbourhoodApiRequest(params: BuildApiRequestParams): NeighbourhoodApiRequest {
  const {
    countryCode,
    zipCode,
    municipalityName,
    latitude,
    longitude,
    region,
    persona,
    locale,
    propertyType,
    listingId,
    poiContext,
    enableWebSearch,
  } = params;

  return {
    listingId,
    location: {
      country: countryCode,
      postalCode: zipCode || '',
      locality: municipalityName,
      latitude,
      longitude,
      region,
    },
    persona: mapPersonaToApi(persona),
    enableWebSearch: enableWebSearch ?? false,
    ...(locale && { locale }),
    objectDescription: propertyType || 'Residential property',
    ...(poiContext && { poiContext }),
  };
}

/**
 * Map API response data to NeighbourhoodInsights interface
 * Handles fallbacks and optional fields
 */
function mapApiResponseToInsights(apiData: any): NeighbourhoodInsights {
  return {
    description:
      apiData.description || apiData.neighbourhoodDescription || 'No description available',
    atmosphere: apiData.atmosphere || apiData.vibe || 'Welcoming neighbourhood',
    lifestyleTags: apiData.lifestyleTags || apiData.tags || [],
    idealFor: apiData.idealFor || [],
    highlights: apiData.highlights || apiData.keyFeatures || [],
    transportHub: apiData.transportHub || {
      quality: 'good',
      description: 'Good public transport access',
    },
    greenSpace: apiData.greenSpace || {
      quality: 'good',
      description: 'Some green spaces available',
    },
    environmentalScores: apiData.environmentalScores,
    ...(apiData.marketData && { marketData: apiData.marketData }),
    ...(apiData.taxData && { taxData: apiData.taxData }),
  };
}

/**
 * Track non-200 API response error in Datadog
 * Tracking temporarily disabled
 */
function trackNon200Error(
  _status: number,
  _municipalityName: string,
  _latitude: number,
  _longitude: number,
): void {
  // Tracking disabled
}

/**
 * Track invalid API response structure error in Datadog
 * Tracking temporarily disabled
 */
function trackInvalidResponseError(_municipalityName: string): void {
  // Tracking disabled
}

/**
 * Track Axios error in Datadog
 * Tracking temporarily disabled
 */
function trackAxiosError(
  _error: any,
  _municipalityName: string,
  _latitude: number,
  _longitude: number,
): void {
  // Tracking disabled
}

/**
 * Handle errors from neighbourhood API calls
 * Throws AbortError for cancellations, returns null for other errors
 */
function handleNeighbourhoodApiError(
  error: unknown,
  municipalityName: string,
  latitude: number,
  longitude: number,
): null {
  if (error instanceof Error && error.name === 'AbortError') {
    throw error;
  }

  if (isAxiosError(error)) {
    if (isCancel(error)) {
      throw new Error('AbortError');
    }
    trackAxiosError(error, municipalityName, latitude, longitude);
  }

  return null;
}

/**
 * Generate neighbourhood insights using POI service data (instead of Google Places)
 * This endpoint uses POI service data that's already fetched by the Travel Time feature,
 * avoiding additional API calls and reducing costs.
 *
 * @param params - Request parameters including location, persona, and POI context
 * @param signal - Optional AbortSignal for cancellation
 * @returns Neighbourhood insights or null if request fails
 */
export async function generateNeighbourhoodInsightsWithPOI(
  params: {
    latitude: number;
    longitude: number;
    municipalityName: string;
    countryCode?: string;
    propertyType?: string;
    zipCode?: string;
    region?: string;
    persona?: string;
    locale?: string;
    poiContext?: POIContext;
    enableWebSearch?: boolean;
    listingId: string;
  },
  signal?: AbortSignal,
): Promise<any> {
  const {
    latitude,
    longitude,
    municipalityName,
    countryCode = 'CH',
    propertyType,
    zipCode,
    region,
    persona,
    locale,
    poiContext,
    enableWebSearch = false,
    listingId,
  } = params;

  try {
    const requestPayload = buildNeighbourhoodApiRequest({
      countryCode,
      zipCode,
      municipalityName,
      latitude,
      longitude,
      region,
      persona,
      locale,
      propertyType,
      listingId,
      poiContext,
      enableWebSearch,
    });

    const response = await axios.post<any>(getNeighbourhoodPOIApiUrl(), requestPayload, {
      headers: { 'Content-Type': 'application/json' },
      timeout: API_TIMEOUT,
      validateStatus: (status) => status < 500,
      signal,
    });

    if (response.status !== 200) {
      trackNon200Error(response.status, municipalityName, latitude, longitude);
      return null;
    }

    return response.data;
  } catch (error) {
    return handleNeighbourhoodApiError(error, municipalityName, latitude, longitude);
  }
}
